import { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchPublicDeviation } from "../../../api/quality/deviation";
import { HtmlTextFormatter, dateToString } from "../../../components/Utils";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceEmpty } from "../../../components/utils/ResourceEmpty";
import { ResourceError } from "../../../components/utils/ResourceError";
import { DocumentViewer } from "../../../components/dashboard/Media/documentViewer";
import { deviationConsequenceOptions } from "../../../components/dashboard/quality/deviations/utils";

const statusLabels = {
  1: "Åpen",
  2: "Under behandling",
  3: "Lukket",
};

const severityLabels = {
  1: "Lav",
  2: "Normal",
  3: "Høy",
  4: "Kritisk",
};

const priorityLabels = {
  1: "Lav",
  2: "Normal",
  3: "Høy",
  4: "Kritisk",
};

const PublicSection = ({ title, children }) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="border-b border-slate-100 px-6 py-4">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
    </div>
    <div className="px-6 py-5">{children}</div>
  </section>
);

export const PublicDeviationCustomerView = () => {
  const { token } = useParams();
  const accessToken = useSelector((state) => state.session.accessToken);
  const currentUser = useSelector((state) => state.session.currentUser);
  const [state, setState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    deviation: null,
  });

  useEffect(() => {
    const loadDeviation = async () => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: false,
        errors: [],
      }));

      try {
        const response = await fetchPublicDeviation(token);
        setState({
          isLoading: false,
          error: false,
          errors: [],
          deviation: response?.data?.data || null,
        });
      } catch (error) {
        setState({
          isLoading: false,
          error: true,
          errors: error?.response?.data?.errors || ["Kunne ikke laste avviket"],
          deviation: null,
        });
      }
    };

    if (token) {
      loadDeviation();
    }
  }, [token]);

  const deviation = state.deviation;

  const consequenceItems = useMemo(() => {
    return deviation?.consequence_items || [];
  }, [deviation]);

  if (accessToken && currentUser?.id && deviation?.id) {
    return <Navigate to={`/dashboard/hse-ks/deviations/${deviation.id}`} replace />;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#dff5ff,_#f8fafc_45%,_#eef2f7)] px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white/90 shadow-xl backdrop-blur">
          <div
            className="px-6 py-8 sm:px-8"
            style={{
              background: `linear-gradient(135deg, ${deviation?.company?.main_color || "#0ea5e9"} 0%, #0f172a 100%)`,
            }}
          >
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="max-w-3xl">
                <p className="text-sm font-medium uppercase tracking-[0.2em] text-white/70">
                  Kundevisning
                </p>
                <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {deviation?.title || deviation?.displayTitle || "Avvik"}
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-white/80">
                  Denne visningen er delt direkte fra Promang og viser informasjon om avviket.
                </p>
              </div>

              {deviation?.company?.logo_url ? (
                <img
                  src={deviation.company.logo_url}
                  alt={deviation.company.name || "Firma"}
                  className="h-14 w-auto rounded-lg bg-white/95 p-2"
                />
              ) : (
                <div className="rounded-xl bg-white/10 px-4 py-3 text-sm font-medium text-white">
                  {deviation?.company?.name || "Promang"}
                </div>
              )}
            </div>

            {deviation && (
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="rounded-full bg-white/15 px-3 py-1.5 text-sm text-white">
                  Status: {statusLabels[deviation.status] || deviation.status || "-"}
                </span>
                {deviation?.severity ? (
                  <span className="rounded-full bg-white/15 px-3 py-1.5 text-sm text-white">
                    Alvorlighet: {severityLabels[deviation.severity] || deviation.severity}
                  </span>
                ) : null}
                {deviation?.priority ? (
                  <span className="rounded-full bg-white/15 px-3 py-1.5 text-sm text-white">
                    Prioritet: {priorityLabels[deviation.priority] || deviation.priority}
                  </span>
                ) : null}
              </div>
            )}
          </div>

          <div className="space-y-6 px-4 py-6 sm:px-8 sm:py-8">
            {state.isLoading ? <ResourceLoading /> : null}
            {!state.isLoading && state.error ? (
              <ResourceError errors={state.errors} />
            ) : null}
            {!state.isLoading && !state.error && !deviation ? <ResourceEmpty /> : null}

            {!state.isLoading && !state.error && deviation ? (
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                  <PublicSection title="Oversikt">
                    <dl className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Avvik
                        </dt>
                        <dd className="mt-1 text-sm text-slate-900">
                          {deviation.displayTitle || "-"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Prosjekt
                        </dt>
                        <dd className="mt-1 text-sm text-slate-900">
                          {deviation.project?.displayTitle || "-"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Kunde
                        </dt>
                        <dd className="mt-1 text-sm text-slate-900">
                          {deviation.customer?.displayTitle || deviation.customer?.name || "-"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Kontakt
                        </dt>
                        <dd className="mt-1 text-sm text-slate-900">
                          {deviation.contact?.displayTitle || deviation.contact?.name || "-"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          Registrert
                        </dt>
                        <dd className="mt-1 text-sm text-slate-900">
                          {deviation.created_at ? dateToString(deviation.created_at) : "-"}
                        </dd>
                      </div>
                    </dl>
                  </PublicSection>

                  <PublicSection title="Forklaring">
                    {deviation.latest_explanation_item?.description ? (
                      <div className="space-y-4">
                        <div className="prose prose-sm max-w-none text-slate-700">
                          <HtmlTextFormatter
                            string={deviation.latest_explanation_item.description}
                          />
                        </div>
                        <DocumentViewer
                          pathType="quickview"
                          documents={deviation.latest_explanation_item.documents}
                        />
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">
                        Det er ikke lagt inn forklaring ennå.
                      </p>
                    )}
                  </PublicSection>

                  <PublicSection title="Konsekvens">
                    {consequenceItems.length === 0 ? (
                      <p className="text-sm text-slate-500">
                        Det er ikke registrert konsekvenser ennå.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {consequenceItems.map((item) => (
                          <div
                            key={item.id}
                            className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4"
                          >
                            <div className="flex flex-wrap items-center gap-3">
                              {item.consequence ? (
                                <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600">
                                  {deviationConsequenceOptions[item.consequence - 1]?.label || "Konsekvens"}
                                </span>
                              ) : null}
                              {item.created_at ? (
                                <span className="text-xs text-slate-500">
                                  {dateToString(item.created_at)}
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-3 text-sm text-slate-700">
                              <HtmlTextFormatter string={item.description} />
                            </div>

                            {item.documents?.length ? (
                              <div className="mt-4">
                                <DocumentViewer
                                  pathType="quickview"
                                  documents={item.documents}
                                />
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </PublicSection>
                </div>

                <div className="space-y-6 lg:col-span-1">
                  <PublicSection title="Om lenken">
                    <div className="space-y-3 text-sm text-slate-600">
                      <p>
                        Denne lenken gir direkte tilgang til kundevisning av avviket.
                      </p>
                      <p>
                        Hvis du er ansatt og allerede logget inn i Promang, blir du sendt videre til den interne avvikssiden.
                      </p>
                    </div>
                  </PublicSection>

                  {deviation.description ? (
                    <PublicSection title="Beskrivelse">
                      <div className="prose prose-sm max-w-none text-slate-700">
                        <HtmlTextFormatter string={deviation.description} />
                      </div>
                    </PublicSection>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
